import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Globe, MapPin, Send, MessageSquare, Instagram, Linkedin, Twitter, Youtube, Target, Wallet, Handshake, Heart } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal, MaskedLines } from "@/components/Motion";
import { WinnerBadges } from "@/components/WinnerBadge";
import { CompanyAvatar } from "@/components/CompanyAvatar";
import { EventCard } from "@/components/EventCard";
import EventDialog from "@/components/EventDialog";
import { BriefCard } from "@/components/BriefCard";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { SampleBadge } from "@/components/SampleBadge";

const SOCIAL_ICONS = { instagram: Instagram, linkedin: Linkedin, x: Twitter, youtube: Youtube };

const Block = ({ icon: Icon, label, children, testid }) => (
  <div className="rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] p-6" data-testid={testid}>
    <p className="kicker flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{label}</p>
    <div className="mt-4 text-slate-200 text-sm leading-relaxed">{children}</div>
  </div>
);

const Tags = ({ items }) =>
  items?.length ? (
    <div className="flex flex-wrap gap-2">{items.map((t) => <span key={t} className="rounded-full border border-[#2A1240]/15 px-3 py-1 text-xs">{t}</span>)}</div>
  ) : <span className="text-slate-400">Not listed yet</span>;

export default function CompanyProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [oppId, setOppId] = useState("");
  const [busy, setBusy] = useState(false);
  const [ev, setEv] = useState(null);
  const [briefs, setBriefs] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [wished, setWished] = useState(false);
  const [rep, setRep] = useState(null);
  useSeo(c?.name ? `${c.name} Partnership Profile` : "Company Profile", c?.about || `Partnership opportunities, packages and sponsorship inventory from ${c?.name || "this company"}.`, `/companies/${id}`);

  useEffect(() => {
    api.get(`/companies/${id}/reputation`).then((r) => setRep(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    api.get("/briefs").then((r) => setBriefs((Array.isArray(r.data) ? r.data : []).filter((b) => b.company_id === id))).catch(() => {});
    api.get(`/companies/${id}/inventory`).then((r) => setInventory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [id]);
  useEffect(() => {
    if (!user || !c) return;
    api.get("/wishlist").then((r) => setWished((r.data || []).some((w) => w.company_id === id || w.name?.toLowerCase() === c.name.toLowerCase()))).catch(() => {});
  }, [user, c, id]);
  const toggleWish = async () => {
    try {
      if (wished) await api.delete(`/wishlist/${encodeURIComponent(c.name)}`); else await api.post("/wishlist", { name: c.name, company_id: id });
      setWished(!wished);
      toast.success(wished ? "Removed from your wishlist" : `${c.name} added to your dream partners`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    api.get(`/companies/${id}`).then((r) => setC(r.data)).catch((e) => setErr(errMsg(e)));
  }, [id]);

  const mine = user && user.company_id === id;
  const guard = (fn) => () => (user ? fn() : nav(`/login?next=/companies/${id}`));

  const openRequest = (opp = "") => {
    setOppId(opp);
    setMsg(opp ? `Hi ${c.name}, we're interested in your opportunity and would love to explore a partnership.` : `Hi ${c.name}, we'd love to explore a partnership with you.`);
    setReqOpen(true);
  };

  const sendRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${id}/request`, { message: msg, opportunity_id: oppId });
      toast.success("Partnership request sent — added to your pipeline");
      setReqOpen(false);
      nav(`/app/messages?thread=${data.thread_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const message = async () => {
    try {
      const { data } = await api.post("/messages/threads", { company_id: id });
      nav(`/app/messages?thread=${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (err) return <div className="min-h-screen bg-[#FFF7FB]"><Nav /><p className="pt-40 text-center text-slate-300" data-testid="company-error">{err}</p></div>;
  if (!c) return <div className="min-h-screen bg-[#FFF7FB]"><Nav /><p className="pt-40 text-center text-slate-400">Loading…</p></div>;

  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="company-profile-page">
      <Nav />
      <section className="relative pt-32 pb-12 overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4">
              <CompanyAvatar c={c} size="h-20 w-20" text="text-3xl" />
              <div>
                <span className="rounded-full border border-[#E0218A]/40 px-3 py-1 text-xs text-[#8E2BC4]" data-testid="company-type-badge">{c.company_type}</span>
                {c.industry && <p className="mt-2 text-sm text-slate-400">{c.industry}</p>}
              </div>
            </div>
            {c.sample && <p className="mt-6 flex flex-wrap items-center gap-2 text-sm text-[#92400E]" data-testid="company-sample-note"><SampleBadge show testid="company-profile-sample" />This is an example profile to show how Balqis Book works. It is not a real company.</p>}
            <MaskedLines as="h1" testid="company-name" className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight" lines={[c.name]} />
            <div className="mt-4"><WinnerBadges companyId={id} testid="company-winner-badges" /></div>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-300 leading-relaxed" data-testid="company-about">{c.about || "This company hasn't written an about section yet."}</p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-300">
              {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#2A1240]" data-testid="company-website-link"><Globe className="h-4 w-4 text-[#E0218A]" />{c.website.replace(/^https?:\/\//, "")}</a>}
              {(c.locations || []).length > 0 && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#E0218A]" />{c.locations.join(" · ")}</span>}
              {Object.entries(c.socials || {}).filter(([, v]) => v).map(([k, v]) => {
                const I = SOCIAL_ICONS[k] || Globe;
                return <a key={k} href={v} target="_blank" rel="noreferrer" className="hover:text-[#2A1240]" aria-label={k} data-testid={`company-social-${k}`}><I className="h-4 w-4" /></a>;
              })}
            </div>
            {Array.isArray(c.audience_stats) && c.audience_stats.length > 0 && (
              <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="company-audience-stats">
                {c.audience_stats.map((s) => (
                  <div key={s.label} className="rounded-xl border border-[#2A1240]/10 bg-white px-4 py-3">
                    <dt className="text-xs text-slate-400">{s.label}</dt>
                    <dd className="mt-1 font-display text-2xl text-[#2A1240]">{s.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3 lg:pt-24">
            {mine ? (
              <Link to="/app/profile" className="btn-gold" data-testid="company-edit-profile-button">Edit your profile</Link>
            ) : (
              <>
                <button className="btn-gold !py-4" onClick={guard(() => openRequest())} data-testid="company-request-partnership-button"><Handshake className="h-4 w-4" />Request partnership</button>
                <button className="btn-ghost !py-4" onClick={guard(message)} data-testid="company-contact-button"><MessageSquare className="h-4 w-4" />Contact</button>
                <button className={`btn-ghost !py-4 ${wished ? "!border-[#E0218A] !text-[#8E2BC4]" : ""}`} onClick={guard(toggleWish)} data-testid="company-wishlist-button"><Heart className={`h-4 w-4 ${wished ? "fill-[#E0218A] text-[#E0218A]" : ""}`} />{wished ? "On your wishlist" : "Add to dream partners"}</button>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Reveal><Block icon={Target} label="Target audience" testid="company-target-audience">{c.target_audience || <span className="text-slate-400">Not listed yet</span>}</Block></Reveal>
        <Reveal delay={0.05}><Block icon={Wallet} label="Budget range" testid="company-budget"><span className="font-mono text-lg text-[#8E2BC4]">{c.budget_range || "—"}</span></Block></Reveal>
        <Reveal delay={0.1}><Block icon={Handshake} label="Partnership interests" testid="company-interests"><Tags items={c.partnership_interests} /></Block></Reveal>
        <Reveal delay={0.15}><Block icon={Send} label="Previous partnerships" testid="company-previous"><Tags items={c.previous_partnerships} /></Block></Reveal>
      </section>

      {rep && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16" data-testid="company-reputation">
          <div className="rounded-3xl border border-[#E0218A]/25 bg-[#FFFFFF] p-7 grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="kicker">Reputation</p>
              {rep.score != null ? <p className="mt-3 font-display text-6xl gold-text" data-testid="reputation-score">{rep.score}<span className="text-2xl text-slate-400"> / 5</span></p> : <p className="mt-3 font-display text-3xl text-[#2A1240]" data-testid="reputation-new">New on Balqis</p>}
              <p className="mt-2 text-sm text-slate-400">{rep.review_count} verified review{rep.review_count === 1 ? "" : "s"} · {rep.completed_deals} completed deal{rep.completed_deals === 1 ? "" : "s"}</p>
              <p className="mt-1 text-xs text-slate-500">Only partners who opened a deal room together can review.</p>
            </div>
            <div className="lg:col-span-4 space-y-2.5">
              {rep.signals.map((s) => (
                <div key={s.label}>
                  <div className="flex justify-between text-sm"><span className="text-slate-300">{s.label}</span><span className="font-mono text-[#2A1240]">{s.value != null ? s.value.toFixed(1) : "—"}</span></div>
                  <div className="mt-1 h-1 rounded-full bg-[#2A1240]/10 overflow-hidden"><div className="h-full bg-[#E0218A]" style={{ width: `${((s.value || 0) / 5) * 100}%` }} /></div>
                </div>
              ))}
            </div>
            <div className="lg:col-span-4 space-y-4">
              {!rep.reviews.length && <p className="text-sm text-slate-400">No reviews yet.</p>}
              {rep.reviews.slice(0, 3).map((r) => <div key={r.id} className="border-l-2 border-[#E0218A]/50 pl-4"><p className="text-sm text-slate-200">“{r.comment || "Great partner."}”</p><p className="mt-1 text-xs text-slate-400">{r.reviewer_company_name} · {r.overall}/5</p></div>)}
            </div>
          </div>
        </section>
      )}
      {briefs.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16" data-testid="company-briefs">
          <h2 className="font-display text-3xl text-[#2A1240]">Partnership briefs</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{briefs.map((b, i) => <BriefCard key={b.id} b={b} i={i} />)}</div>
        </section>
      )}
      {inventory.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16" data-testid="company-inventory">
          <h2 className="font-display text-3xl text-[#2A1240]">Sponsorship inventory</h2>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {inventory.map((i) => (
              <div key={i.id} className="rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] p-5" data-testid={`company-inventory-${i.id}`}>
                <p className="text-sm text-slate-300">{i.asset}</p>
                <p className="mt-2 font-display text-3xl text-[#2A1240]">{i.available}<span className="text-base text-slate-400"> / {i.total} available</span></p>
              </div>
            ))}
          </div>
        </section>
      )}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-16" data-testid="company-opportunities">
        <h2 className="font-display text-3xl text-[#2A1240]">Open opportunities</h2>
        <div className="mt-6 divide-y divide-[#2A1240]/10 border-y border-[#2A1240]/10">
          {!c.opportunities?.length && <p className="py-8 text-slate-400">No open opportunities right now.</p>}
          {(c.opportunities || []).map((o) => (
            <div key={o.id} className="py-6 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid={`company-opportunity-${o.id}`}>
              <div>
                <p className="font-display text-xl text-[#2A1240]">{o.title}</p>
                <p className="mt-1 text-sm text-slate-400">{o.description}</p>
                <p className="mt-2 text-xs text-slate-400">{(o.tags || []).join(" · ")} {o.deadline && `· by ${fmtDate(o.deadline)}`}</p>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="font-mono text-[#8E2BC4]">{o.budget}</span>
                {!mine && <button className="btn-ghost !py-2" onClick={guard(() => openRequest(o.id))} data-testid={`apply-opportunity-${o.id}`}>Request</button>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {c.events?.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
          <h2 className="font-display text-3xl text-[#2A1240]">Events</h2>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{c.events.map((e, i) => <EventCard key={e.id} e={e} i={i} onOpen={(x) => api.get(`/events/${x.id}`).then((r) => setEv(r.data)).catch(() => setEv(x))} />)}</div>
        </section>
      )}

      <Dialog open={reqOpen} onOpenChange={setReqOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10" data-testid="request-dialog">
          <DialogTitle className="font-display text-2xl font-normal text-[#2A1240]">Request a partnership</DialogTitle>
          <DialogDescription className="text-slate-400">Your message opens a conversation with {c.name} and adds them to your pipeline.</DialogDescription>
          <textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} className="field" data-testid="request-message-input" />
          <button className="btn-gold" disabled={busy || !msg.trim()} onClick={sendRequest} data-testid="request-submit-button">{busy ? "Sending…" : "Send request"}</button>
        </DialogContent>
      </Dialog>
      <EventDialog event={ev} onClose={() => setEv(null)} />
      <Footer />
    </div>
  );
}
